import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Autocomplete, MenuItem, Stack, Typography, Box, Chip,
} from '@mui/material';
import { COMPONENT_STATUSES, COMPONENT_STATUS_LABEL } from '../../utils/constants';

// FEAT-003 ComponentEditDialog — Badge 클릭 편집 모달.
// SW 는 식별자 불변 (read-only Chip). 버전 / 상태 / notes 만 변경 가능.
//
// Props:
//   open       : boolean
//   onClose    : 모달 닫기
//   component  : 현재 component { product, version, status, notes }
//   softwares  : SW 메타 list (버전 자동 완성 옵션 소스)
//   onSave     : (updatedComponent) => void
export default function ComponentEditDialog({ open, onClose, component, softwares, onSave }) {
  const [version, setVersion] = useState('');
  const [status, setStatus] = useState('planned');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open || !component) return;
    setVersion(component.version || '');
    setStatus(component.status || 'planned');
    setNotes(component.notes || '');
  }, [open, component]);

  if (!component) return null;

  const meta = softwares.find((s) => s.id === component.product);
  const versionOptions = [];
  if (meta?.versions?.latest?.tag) {
    versionOptions.push(meta.versions.latest.tag);
  }
  (meta?.versions?.planned || []).forEach((p) => {
    if (p.tag) versionOptions.push(p.tag);
  });
  if (component.version && !versionOptions.includes(component.version)) {
    versionOptions.unshift(component.version);
  }

  const handleSave = () => {
    onSave({
      ...component,
      version: version.trim(),
      status,
      notes: notes.trim(),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>구성 소프트웨어 편집</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>소프트웨어</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={`${meta?.name || component.product} (${component.product})`}
                size="small"
                sx={{ fontFamily: 'JetBrains Mono, monospace' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
              소프트웨어 식별자는 변경 불가. 신규 소프트웨어 연결은 행 메뉴 "추가" 사용
            </Typography>
          </Box>

          <Autocomplete
            freeSolo
            size="small"
            options={versionOptions}
            value={version}
            onChange={(_, v) => setVersion(typeof v === 'string' ? v : '')}
            onInputChange={(_, v, reason) => { if (reason === 'input') setVersion(v); }}
            renderInput={(params) => (
              <TextField {...params} label="버전" placeholder="예: v4.0.0 또는 TBD" />
            )}
          />

          <TextField
            select
            size="small"
            label="상태"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {COMPONENT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {COMPONENT_STATUS_LABEL[s]} ({s})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="메모 (선택)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            placeholder="자유 메모 (PR 링크 등)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={handleSave}>저장 → buffer 에 추가</Button>
      </DialogActions>
    </Dialog>
  );
}
